import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { LoadingSpinner } from '@/components/ui/loading'
import { useAuthStore } from '@/stores/auth'
import { useToast } from '@/hooks/use-toast'

export function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const { signIn, isLoading } = useAuthStore()
  const { toast } = useToast()
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email || !password) {
      toast({
        title: 'Fehler',
        description: 'Bitte füllen Sie alle Felder aus.',
        variant: 'destructive'
      })
      return
    }

    try {
      await signIn(email, password)
      toast({
        title: 'Erfolgreich angemeldet',
        description: 'Willkommen zurück!'
      })
      navigate('/dashboard')
    } catch (error) {
      toast({
        title: 'Anmeldung fehlgeschlagen',
        description: 'Bitte überprüfen Sie Ihre Anmeldedaten.',
        variant: 'destructive'
      })
    }
  }

  // Admin login for development
  const handleDemoLogin = () => {
    setEmail('admin@mission-solar.at')
    setPassword('solarZZZ27!')

    // Simulate successful login by navigating directly
    setTimeout(() => {
      toast({
        title: 'Demo-Modus',
        description: 'Sie sind im Demo-Modus angemeldet.'
      })
      navigate('/dashboard')
    }, 1000)
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-12 h-12 mx-auto mb-4">
            <img src="/ms_icon_light.svg" alt="Mission Solar Logo" className="w-full h-full object-contain" />
          </div>
          <CardTitle className="text-2xl">Mission Solar</CardTitle>
          <p className="text-muted-foreground">Melden Sie sich in Ihrem Konto an</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-Mail</Label>
              <Input
                id="email"
                type="email"
                placeholder="ihre@email.de"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Passwort</Label>
              <Input
                id="password"
                type="password"
                placeholder="Ihr Passwort"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
              />
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Anmelden...
                </>
              ) : (
                'Anmelden'
              )}
            </Button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                Oder
              </span>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full"
            onClick={handleDemoLogin}
            disabled={isLoading}
          >
            Demo-Zugang
          </Button>

          <div className="text-center text-sm space-y-2">
            <Link to="/auth/reset-password" className="text-primary hover:underline block">
              Passwort vergessen?
            </Link>
            <div className="text-muted-foreground">
              Noch kein Konto?{' '}
              <Link to="/auth/register" className="text-primary hover:underline">
                Registrieren
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}